import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import InfoIcon from "@mui/icons-material/Info";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import AlertComponent from "../../subcomponents/AlertComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { retailCategoryLinkingTooltip } from "../../../constants/TooltipInformation";
import { RetailPropertyEnum } from "../../../constants/NavbarList";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const RetailCategoryLinking = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [mainCategory, setMainCategory] = useState<string | null>(null);
  const [subcategory1, setSubcategory1] = useState<string | null>(null);
  const [subcategory2, setSubcategory2] = useState<string | null>(null);
  const [subcategory3, setSubcategory3] = useState<string | null>(null);
  const [subcategory4, setSubcategory4] = useState<string | null>(null);
  const [subcategory5, setSubcategory5] = useState<string | null>(null);
  const [mainCategoryErr, setMainCategoryErr] = useState<boolean>(false);
  const [sub1Err, setSub1Err] = useState<boolean>(false);
  const [sub2Err, setSub2Err] = useState<boolean>(false);
  const [sub3Err, setSub3Err] = useState<boolean>(false);
  const [sub4Err, setSub4Err] = useState<boolean>(false);
  const [sub5Err, setSub5Err] = useState<boolean>(false);
  const [retailCategoryLinking, setRetailCategoryLinking] = useState<any>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const payload = {
    "Main Category": mainCategory,
    "Subcategory 1": subcategory1 ?? "",
    "Subcategory 2": subcategory2 ?? "",
    "Subcategory 3": subcategory3 ?? "",
    "Subcategory 4": subcategory4 ?? "",
    "Subcategory 5": subcategory5 ?? "",
  };

  const validateInput = () => {
    setMainCategoryErr(false);
    setSub1Err(false);
    setSub2Err(false);
    setSub3Err(false);
    setSub4Err(false);
    setSub5Err(false);
    if (
      mainCategory &&
      !validateString(
        mainCategory,
        validationLength.golfCategoryName,
        validationType.SPACE,
      )
    ) {
      setMainCategoryErr(true);
      return false;
    }
    if (
      subcategory1 &&
      !validateString(
        subcategory1,
        validationLength.subCategoryName,
        validationType.SPACE,
      )
    ) {
      setSub1Err(true);
      return false;
    }
    if (
      subcategory2 &&
      !validateString(
        subcategory2,
        validationLength.subCategoryName,
        validationType.SPACE,
      )
    ) {
      setSub2Err(true);
      return false;
    }
    if (
      subcategory3 &&
      !validateString(
        subcategory3,
        validationLength.subCategoryName,
        validationType.SPACE,
      )
    ) {
      setSub3Err(true);
      return false;
    }
    if (
      subcategory4 &&
      !validateString(
        subcategory4,
        validationLength.subCategoryName,
        validationType.SPACE,
      )
    ) {
      setSub4Err(true);
      return false;
    }
    if (
      subcategory5 &&
      !validateString(
        subcategory5,
        validationLength.subCategoryName,
        validationType.SPACE,
      )
    ) {
      setSub5Err(true);
      return false;
    }
    return true;
  };

  const editRetailCategoryLinking = (list: any) => {
    if (list == null) {
      setSelectedId(null);
      setEditClicked(false);
      setMainCategory(null);
      setSubcategory1(null);
      setSubcategory2(null);
      setSubcategory3(null);
      setSubcategory4(null);
      setSubcategory5(null);
      setMainCategoryErr(false);
      setSub1Err(false);
      setSub2Err(false);
      setSub3Err(false);
      setSub4Err(false);
      setSub5Err(false);
      return;
    }
    if (list !== null) {
      setEditClicked(true);
      if (list.id != null) {
        setSelectedId(list.id);
      } else {
        setSelectedId(null);
      }
    }
    setMainCategory(list?.["Main Category"] ?? null);
    setSubcategory1(
      list?.["Subcategory 1"] != null && list["Subcategory 1"] !== ""
        ? String(list["Subcategory 1"])
        : null,
    );
    setSubcategory2(
      list?.["Subcategory 2"] != null && list["Subcategory 2"] !== ""
        ? String(list["Subcategory 2"])
        : null,
    );
    setSubcategory3(
      list?.["Subcategory 3"] != null && list["Subcategory 3"] !== ""
        ? String(list["Subcategory 3"])
        : null,
    );
    setSubcategory4(
      list?.["Subcategory 4"] != null && list["Subcategory 4"] !== ""
        ? String(list["Subcategory 4"])
        : null,
    );
    setSubcategory5(
      list?.["Subcategory 5"] != null && list["Subcategory 5"] !== ""
        ? String(list["Subcategory 5"])
        : null,
    );
  };

  const updateRetailCategoryLinking = () => {
    if (!mainCategory || String(mainCategory).trim() === "") {
      setAlertMessage("Main Category is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (!validateInput()) return;
    const linkKey = `${mainCategory ?? ""}|${subcategory1 ?? ""}|${subcategory2 ?? ""}|${subcategory3 ?? ""}|${subcategory4 ?? ""}|${subcategory5 ?? ""}`;
    if (
      checkDuplicate(
        retailCategoryLinking
          .filter((item: any) => String(item.id) !== String(selectedId))
          .map((item: any) => ({
            ...item,
            LinkKey: `${item["Main Category"] ?? ""}|${item["Subcategory 1"] ?? ""}|${item["Subcategory 2"] ?? ""}|${item["Subcategory 3"] ?? ""}|${item["Subcategory 4"] ?? ""}|${item["Subcategory 5"] ?? ""}`,
          })),
        "LinkKey",
        linkKey,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setMainCategoryErr(false);
    setSub1Err(false);
    setSub2Err(false);
    setSub3Err(false);
    setSub4Err(false);
    setSub5Err(false);
    setRetailCategoryLinking((prev: any) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editRetailCategoryLinking(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${RetailPropertyEnum.categoryLinking} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addRetailCategoryLinking = () => {
    if (!mainCategory || String(mainCategory).trim() === "") {
      setAlertMessage("Main Category is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (!validateInput()) return;
    const linkKey = `${mainCategory ?? ""}|${subcategory1 ?? ""}|${subcategory2 ?? ""}|${subcategory3 ?? ""}|${subcategory4 ?? ""}|${subcategory5 ?? ""}`;
    if (
      checkDuplicate(
        retailCategoryLinking.map((item: any) => ({
          ...item,
          LinkKey: `${item["Main Category"] ?? ""}|${item["Subcategory 1"] ?? ""}|${item["Subcategory 2"] ?? ""}|${item["Subcategory 3"] ?? ""}|${item["Subcategory 4"] ?? ""}|${item["Subcategory 5"] ?? ""}`,
        })),
        "LinkKey",
        linkKey,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRetailCategoryLinking((prev: any) =>
      updateData(
        prev !== null && prev?.length
          ? [
              ...prev,
              {
                id: retailCategoryLinking.length + 1,
                ...payload,
              },
            ]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editRetailCategoryLinking(null);
    setEditClicked(false);
  };

  const deleteRetailCategoryLinking = (id: number) => {
    setRetailCategoryLinking(deleteItem(retailCategoryLinking, id));
  };

  useEffect(() => {
    data(retailCategoryLinking);
    editClicked &&
      setSelectedId(
        checkData(retailCategoryLinking, selectedId) ? selectedId : null,
      );
  }, [retailCategoryLinking]);

  useEffect(() => {
    if (fieldValues !== undefined && fieldValues !== null) {
      setRetailCategoryLinking(fieldValues);
    }
  }, []);

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Category Linking
              <Tooltip
                title={tooltipComponent(retailCategoryLinkingTooltip.header)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton click={() => editRetailCategoryLinking(null)} />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editRetailCategoryLinking(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Main Category
              <Tooltip
                title={tooltipComponent(
                  retailCategoryLinkingTooltip.mainCategory,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailCategoryLinkingMainCategory"
              variant="outlined"
              placeholder="Main Category"
              value={mainCategory ?? ""}
              helperText={
                mainCategoryErr ? validationMessages.golfCategoryName : ""
              }
              error={mainCategoryErr}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setMainCategory(e.target.value || null);
                setMainCategoryErr(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Subcategory 1
              <Tooltip
                title={tooltipComponent(
                  retailCategoryLinkingTooltip.subcategory1,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailCategoryLinkingSub1"
              variant="outlined"
              placeholder="Subcategory 1"
              value={subcategory1 ?? ""}
              helperText={sub1Err ? validationMessages.subCategoryName : ""}
              error={sub1Err}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSubcategory1(e.target.value || null);
                setSub1Err(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Subcategory 2
              <Tooltip
                title={tooltipComponent(
                  retailCategoryLinkingTooltip.subcategory2,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailCategoryLinkingSub2"
              variant="outlined"
              placeholder="Subcategory 2"
              value={subcategory2 ?? ""}
              helperText={sub2Err ? validationMessages.subCategoryName : ""}
              error={sub2Err}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSubcategory2(e.target.value || null);
                setSub2Err(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Subcategory 3
              <Tooltip
                title={tooltipComponent(
                  retailCategoryLinkingTooltip.subcategory3,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailCategoryLinkingSub3"
              variant="outlined"
              placeholder="Subcategory 3"
              value={subcategory3 ?? ""}
              helperText={sub3Err ? validationMessages.subCategoryName : ""}
              error={sub3Err}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSubcategory3(e.target.value || null);
                setSub3Err(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Subcategory 4
              <Tooltip
                title={tooltipComponent(
                  retailCategoryLinkingTooltip.subcategory4,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailCategoryLinkingSub4"
              variant="outlined"
              placeholder="Subcategory 4"
              value={subcategory4 ?? ""}
              helperText={sub4Err ? validationMessages.subCategoryName : ""}
              error={sub4Err}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSubcategory4(e.target.value || null);
                setSub4Err(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Subcategory 5
              <Tooltip
                title={tooltipComponent(
                  retailCategoryLinkingTooltip.subcategory5,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailCategoryLinkingSub5"
              variant="outlined"
              placeholder="Subcategory 5"
              value={subcategory5 ?? ""}
              helperText={sub5Err ? validationMessages.subCategoryName : ""}
              error={sub5Err}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSubcategory5(e.target.value || null);
                setSub5Err(false);
              }}
            />
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={
                selectedId
                  ? updateRetailCategoryLinking
                  : addRetailCategoryLinking
              }
              id="addRetailCategoryLinking"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Category
              Linking
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Category Linking Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {retailCategoryLinking?.length ? (
                <DraggableItemList
                  itemData={retailCategoryLinking}
                  onOrderUpdate={(order) => setRetailCategoryLinking(order)}
                  onEdit={(info) => {
                    editRetailCategoryLinking(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteRetailCategoryLinking(id)}
                  code={"Main Category"}
                  name={"Subcategory 1"}
                />
              ) : (
                <Typography variant="body2">
                  No Category Linking Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RetailCategoryLinking;
